import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Box, Chip, CircularProgress } from "@mui/material";
import { InputSecret } from "../../../common/controls";
import { useFormRecord } from "../../../../common/controls/forms/FormRecordContext";
import { useRootStore } from "../../../../store/contexts";
import { restFetch } from "../../../../api/apiClient";
import { credentialState } from "../../../common/validation/hasCredential";

/**
 * Pieza compartida por los cuatro componentes custom de credenciales de
 * TadManagerConfig. Cada uno decide su propia visibilidad según el proveedor
 * elegido y delega acá el resto: el campo en sí más el indicador de "credencial
 * cargada".
 *
 * El indicador existe porque esta pantalla administra credenciales que el
 * server nunca devuelve: el campo se ve siempre vacío, así que sin él no hay
 * forma de distinguir "hay una guardada" de "no hay ninguna" salvo pisándola.
 */

export type CredentialFieldName =
  | "procedureNumber"
  | "miArgentinaPassword"
  | "arcaPassword"
  | "ansesPassword";

type HasValueMap = Partial<Record<`${CredentialFieldName}HasValue`, boolean>>;

// El form genérico expone a los customComponent los valores vivos de los campos
// hermanos, pero no los `<campo>HasValue`: hay que preguntarlos por recordId.
const HAS_VALUE_QUERY = `query TadManagerConfigCredentialsHasValue($id: String!) {
  tadManagerConfigOne(id: $id) {
    procedureNumberHasValue
    miArgentinaPasswordHasValue
    arcaPasswordHasValue
    ansesPasswordHasValue
  }
}`;

/**
 * Presencia de la credencial como tri-estado (`undefined` = todavía no se
 * sabe). Mientras el pedido no resolvió —o falló, o no hay registro— no se
 * afirma nada: lo tecleado en este formulario es lo único que puede darla por
 * presente antes de que llegue la respuesta.
 */
const useCredentialState = (
  field: CredentialFieldName,
  typed: string | null | undefined
): boolean | undefined => {
  const { recordId } = useFormRecord();
  const [saved, setSaved] = useState<HasValueMap | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    if (!recordId) {
      setSaved(undefined);
      return;
    }
    (async () => {
      try {
        const data = await restFetch<{
          data?: { tadManagerConfigOne?: HasValueMap | null };
        }>("/graphql", {
          method: "POST",
          body: JSON.stringify({
            query: HAS_VALUE_QUERY,
            variables: { id: recordId },
          }),
        });
        if (alive) setSaved(data?.data?.tadManagerConfigOne ?? {});
      } catch {
        if (alive) setSaved(undefined);
      }
    })();
    return () => {
      alive = false;
    };
  }, [recordId]);

  return credentialState(
    saved ? Boolean(saved[`${field}HasValue`]) : undefined,
    typed
  );
};

const CredentialStatus: React.FC<{ state: boolean | undefined }> = observer(
  ({ state }) => {
    const { langStore } = useRootStore();
    // Tres estados visualmente distintos: cargada, nada (no hay), y el
    // desconocimiento, que no se dibuja como ausencia.
    if (state === undefined) return <CircularProgress size={14} thickness={5} />;
    if (!state) return null;
    return (
      <Chip
        variant="outlined"
        size="small"
        label={langStore.tadManagerAuthProviderCredentialLoaded}
      />
    );
  }
);

export interface CredentialFieldProps {
  field: CredentialFieldName;
  value: any;
  onChange: (value: any) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  description?: string;
}

export const CredentialField: React.FC<CredentialFieldProps> = ({
  field,
  value,
  onChange,
  error,
  helperText,
  label,
  description,
}) => {
  const state = useCredentialState(field, value);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <InputSecret
        value={value ?? ""}
        error={Boolean(error)}
        helperText={helperText}
        label={label || ""}
        onChange={(v: string) => onChange(v)}
        description={description}
      />
      <CredentialStatus state={state} />
    </Box>
  );
};
